import React, { useMemo } from 'react';
import {
  Activity, ImageUp, KeyRound, Link2, ListChecks, PackagePlus, Rocket, Settings2, ShieldAlert, Sparkles, Trash2,
  UserCog, UserMinus, UserPlus,
} from 'lucide-react';
import { parseUtc, timeAgo } from '../webhookHealth';
import './Activity.css';

const WEEK_MS = 7 * 86400000;

// Readable names for the actions the backend logs (see logActivity in backend/src/index.js)
const ACTIONS = {
  login_failed: { label: 'Failed login', icon: ShieldAlert, tone: 'danger' },
  create_user: { label: 'User created', icon: UserPlus },
  delete_user: { label: 'User deleted', icon: UserMinus, tone: 'danger' },
  change_role: { label: 'Role changed', icon: UserCog },
  reset_password: { label: 'Password reset', icon: KeyRound },
  create_order: { label: 'Order created', icon: PackagePlus },
  bulk_status_update: { label: 'Orders updated', icon: ListChecks },
  bulk_delete: { label: 'Orders deleted', icon: Trash2, tone: 'danger' },
  update_settings: { label: 'Settings updated', icon: Settings2 },
  migrate_images: { label: 'Images moved to R2', icon: ImageUp },
  ai_detect_bins: { label: 'Bins detected with AI', icon: Sparkles },
  update_product_photo: { label: 'Product photo set', icon: ImageUp },
  remove_product_photo: { label: 'Product photo removed', icon: Trash2 },
  register: { label: 'Account created', icon: Rocket },
  create_invite: { label: 'Invite link created', icon: Link2 },
  revoke_invite: { label: 'Invite link revoked', icon: Link2, tone: 'danger' },
  join_invite: { label: 'Joined by invite', icon: UserPlus },
  login_google: { label: 'Signed in with Google', icon: KeyRound },
  password_reset_requested: { label: 'Password reset link sent', icon: KeyRound },
  password_reset: { label: 'Password reset by email', icon: KeyRound },
  store_disconnected: { label: 'Store disconnected', icon: Link2, tone: 'danger' },
  privacy_request: { label: 'Store privacy request', icon: ShieldAlert },
  import_products: { label: 'Products imported', icon: PackagePlus },
  store_connected: { label: 'Store connected', icon: Link2 },
  store_marked_shipped: { label: 'Marked shipped in store', icon: ListChecks },
  store_update_failed: { label: "Couldn't update store", icon: ShieldAlert, tone: 'danger' },
  rename_product: { label: 'Product renamed', icon: Settings2 },
};

// "some_new_action" -> "Some new action" for anything not listed above
const humanize = (action = '') => {
  const words = action.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const actionInfo = (action) => ACTIONS[action] || { label: humanize(action), icon: Activity };

const formatAbsolute = (date) => date.toLocaleString(undefined, {
  weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

// Relative time ("5 min ago") with the exact date and time on hover
export function RelativeTime({ value }) {
  if (!value) return null;
  const date = parseUtc(value);
  const label = Date.now() - date < WEEK_MS
    ? timeAgo(value)
    : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  return <time dateTime={date.toISOString()} title={formatAbsolute(date)}>{label}</time>;
}

const dayLabel = (date) => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long',
    year: date.getFullYear() === today.getFullYear() ? undefined : 'numeric',
  });
};

function ActivityEntry({ log, compact }) {
  const { label, icon: Icon, tone } = actionInfo(log.action);
  return (
    <li className={`activity-entry ${tone ? `activity-entry-${tone}` : ''}`}>
      <span className="activity-entry-icon" aria-hidden="true">
        <Icon size={16} />
      </span>
      <div className="activity-entry-body">
        <div className="activity-entry-top">
          <span className="activity-entry-title">{label}</span>
          <span className="activity-entry-time"><RelativeTime value={log.created_at} /></span>
        </div>
        {log.details && <p className={`activity-entry-details ${compact ? 'is-clamped' : ''}`}>{log.details}</p>}
        <div className="activity-entry-user">{log.user_email || 'System'}</div>
      </div>
    </li>
  );
}

// List of activity log rows. `grouped` adds day headings (Today, Yesterday, ...);
// `compact` tightens spacing and clamps long details (dashboard card).
export function ActivityList({ logs, grouped = false, compact = false }) {
  const groups = useMemo(() => {
    if (!grouped) return [{ key: 'all', logs }];
    const result = [];
    for (const log of logs) {
      const label = dayLabel(parseUtc(log.created_at));
      const last = result[result.length - 1];
      if (last?.label === label) last.logs.push(log);
      else result.push({ key: label, label, logs: [log] });
    }
    return result;
  }, [logs, grouped]);

  return (
    <div className={`activity-list ${compact ? 'activity-list-compact' : ''}`}>
      {groups.map(group => (
        <section key={group.key} className="activity-group" aria-label={group.label}>
          {group.label && <h2 className="activity-day">{group.label}</h2>}
          <ul className="activity-entries">
            {group.logs.map(log => <ActivityEntry key={log.id} log={log} compact={compact} />)}
          </ul>
        </section>
      ))}
    </div>
  );
}
